/**
 * J.6 — Session History export.
 *
 * Wires the Settings → Data → "Export Session History" button to a real
 * client-side download. Reads the signed-in user's data from Convex
 * (audit logs, pending writes, training runs, vault notes), packages it
 * as a single JSON file, and triggers a browser download.
 *
 * Scope is intentionally user-data only. portalAuditLog rows are
 * operator-internal (admin-guarded in `convex/portalAuditLog.ts:listForSlug`)
 * so the export records that they're skipped rather than failing — a
 * non-operator user shouldn't see operator audit history. The shape
 * keeps a stable `tables` key so future additions append cleanly.
 *
 * Decisions row: future-plans/decisions.md 2026-05-19 J.6 — JSON shape.
 *
 * Layer 1 unit-tested with mocked ConvexReactClient. The download trigger
 * is split into `assembleSessionHistory()` (pure, testable) and
 * `triggerDownload()` (DOM side-effect).
 */

import type { ConvexReactClient } from 'convex/react';
import { api } from '../../../convex/_generated/api';

export interface SessionHistoryMetadata {
  exportedAt: string; // ISO 8601
  portalSlug: string;
  userId: string | null;
  schemaVersion: 1;
}

export interface SessionHistoryTable {
  /** Source Convex table (live). */
  table: string;
  /** Rows captured. Empty array when scope had no data. */
  rows: ReadonlyArray<Record<string, unknown>>;
  /** When access was refused (e.g. operator-only table for a non-operator). */
  skipped?: { reason: string };
}

export interface SessionHistoryBundle {
  metadata: SessionHistoryMetadata;
  tables: Record<string, SessionHistoryTable>;
}

export interface AssembleArgs {
  convex: Pick<ConvexReactClient, 'query'>;
  portalSlug: string;
  userId: string | null;
  /** Override exportedAt for deterministic tests. */
  now?: () => Date;
}

/**
 * Pure-ish assembly: runs the Convex queries, collects rows, builds the
 * bundle. Catches per-table errors so a single permission denial doesn't
 * sink the whole export.
 */
export async function assembleSessionHistory(
  args: AssembleArgs,
): Promise<SessionHistoryBundle> {
  const { convex, portalSlug } = args;
  const exportedAt = (args.now ?? (() => new Date()))().toISOString();

  const tables: Record<string, SessionHistoryTable> = {};

  // nipeAuditLog — user-scoped chat events for this portal.
  await collect(tables, 'nipeAuditLog', async () => {
    const rows = await convex.query(api.nipeAuditLog.listForSlug, {
      clientSlug: portalSlug,
    });
    return rows as ReadonlyArray<Record<string, unknown>>;
  });

  // portalAuditLog — admin-only. Non-operators get a "skipped" marker
  // instead of a hard failure. Operators see real rows.
  await collect(tables, 'portalAuditLog', async () => {
    const rows = await convex.query(api.portalAuditLog.listForSlug, {
      clientSlug: portalSlug,
    });
    return rows as ReadonlyArray<Record<string, unknown>>;
  });

  // nipePendingWrites — user-scoped SP write proposals for this portal.
  await collect(tables, 'nipePendingWrites', async () => {
    const rows = await convex.query(api.nipePendingWrites.listForSlug, {
      clientSlug: portalSlug,
    });
    return rows as ReadonlyArray<Record<string, unknown>>;
  });

  // tinkerSpikeRuns — user-scoped LoRA training runs. The listRecent
  // query is keyed by userId server-side, so this naturally returns
  // only the caller's runs.
  await collect(tables, 'tinkerSpikeRuns', async () => {
    const rows = await convex.query(api.tinkerSpikeRuns.listRecent, {
      limit: 50,
    });
    return rows as ReadonlyArray<Record<string, unknown>>;
  });

  // Vault notes — list the portal's vaults, then aggregate notes across
  // each. listVaults is portal-scoped (clientSlug arg), and listNotesByVault
  // is vault-scoped, so the result is the union of every note in this
  // portal's vaults.
  await collect(tables, 'vaultNotes', async () => {
    const vaults = (await convex.query(api.vaults.listVaults, {
      clientSlug: portalSlug,
    })) as ReadonlyArray<{ _id: string }>;
    const all: Record<string, unknown>[] = [];
    for (const vault of vaults) {
      const notes = (await convex.query(api.vaults.listNotesByVault, {
        vaultId: String(vault._id),
      })) as ReadonlyArray<Record<string, unknown>>;
      for (const n of notes) all.push(n);
    }
    return all;
  });

  const bundle: SessionHistoryBundle = {
    metadata: {
      exportedAt,
      portalSlug,
      userId: args.userId,
      schemaVersion: 1,
    },
    tables,
  };
  return bundle;
}

async function collect(
  out: Record<string, SessionHistoryTable>,
  table: string,
  fetcher: () => Promise<ReadonlyArray<Record<string, unknown>>>,
): Promise<void> {
  try {
    const rows = await fetcher();
    out[table] = { table, rows };
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    out[table] = { table, rows: [], skipped: { reason } };
  }
}

/**
 * Render the bundle as a downloadable JSON blob. Pure; no DOM access.
 */
export function bundleToBlob(bundle: SessionHistoryBundle): Blob {
  const json = JSON.stringify(bundle, null, 2);
  return new Blob([json], { type: 'application/json' });
}

/**
 * Derive the canonical filename. Format:
 *   repancy-session-history-${slug}-${YYYY-MM-DD}.json
 */
export function downloadFilename(
  bundle: Pick<SessionHistoryBundle, 'metadata'>,
): string {
  const slug = bundle.metadata.portalSlug;
  const day = bundle.metadata.exportedAt.slice(0, 10); // ISO date portion
  return `repancy-session-history-${slug}-${day}.json`;
}

/**
 * Trigger the actual browser download. Splits assembly from side-effects
 * so the assembly half stays unit-testable. Constructs an anchor, clicks
 * it, then revokes the object URL — the standard SPA download pattern.
 */
export function triggerDownload(bundle: SessionHistoryBundle): void {
  const blob = bundleToBlob(bundle);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = downloadFilename(bundle);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke a tick so Safari completes the download attach.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * One-shot helper used by the SettingsWindow click handler. Assembles +
 * triggers in a single call.
 */
export async function exportSessionHistory(
  args: AssembleArgs,
): Promise<SessionHistoryBundle> {
  const bundle = await assembleSessionHistory(args);
  triggerDownload(bundle);
  return bundle;
}
